import type { Atlas } from '../assets/atlas';

export interface SoundSpriteCacheOptions {
  readonly atlas: Atlas;
  readonly prefix: string;
  readonly fetchAudio: (url: string) => Promise<ArrayBuffer>;
  readonly decode: (data: ArrayBuffer) => Promise<AudioBuffer>;
}

export interface Clip {
  readonly buffer: AudioBuffer;
  readonly offset: number;
  readonly duration: number;
}

type Entry = { state: 'loading' } | { state: 'ready'; buffer: AudioBuffer } | { state: 'failed' };

export class SoundSpriteCache {
  private readonly sprites = new Map<string, Entry>();
  private owner: Map<string, string> | null = null;

  constructor(private readonly options: SoundSpriteCacheOptions) {}

  preload(): void {
    for (const name of this.options.atlas.soundSpriteNames) {
      if (this.options.atlas.soundSprite(name)?.eager === true) this.request(name);
    }
  }

  get(name: string): Clip | null {
    const spriteName = this.locate(name);
    if (spriteName === null) return null;

    const entry = this.sprites.get(spriteName);
    if (entry === undefined) {
      this.request(spriteName);
      return null;
    }
    if (entry.state !== 'ready') return null;

    const clip = this.options.atlas.soundSprite(spriteName)?.clips[name];
    if (clip === undefined) return null;
    return { buffer: entry.buffer, offset: clip.offset, duration: clip.duration };
  }

  private locate(clipName: string): string | null {
    if (this.owner === null) {
      const names = this.options.atlas.soundSpriteNames;
      if (names.length === 0) return null;
      this.owner = new Map();
      for (const spriteName of names) {
        const sprite = this.options.atlas.soundSprite(spriteName);
        if (sprite === null) continue;
        for (const clip of Object.keys(sprite.clips)) this.owner.set(clip, spriteName);
      }
    }
    return this.owner.get(clipName) ?? null;
  }

  private request(spriteName: string): void {
    if (this.sprites.has(spriteName)) return;
    const sprite = this.options.atlas.soundSprite(spriteName);
    if (sprite === null) return;
    this.sprites.set(spriteName, { state: 'loading' });
    void this.load(spriteName, `${this.options.prefix}/${sprite.file}`);
  }

  private async load(spriteName: string, url: string): Promise<void> {
    try {
      const buffer = await this.options.decode(await this.options.fetchAudio(url));
      this.sprites.set(spriteName, { state: 'ready', buffer });
    } catch {
      /** Remembered as failed so a missing sprite is not re-requested on every cue. */
      this.sprites.set(spriteName, { state: 'failed' });
    }
  }
}

export async function fetchAudioBytes(url: string): Promise<ArrayBuffer> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url}: ${String(response.status)}`);
  return await response.arrayBuffer();
}
